import { supabase, tableName } from './supabase';
import { currentUserId } from './user';
import { recordUserAction } from './user-actions';
import { logCaughtError } from './error-log';
import { gewichteterEinstieg } from './v6/pyramide';

import type {
  AssetClass,
  BriefingHistoryEntry,
  ExitReason,
  JournalActionType,
  JournalEntry,
  JournalTag,
  LessonsLearned,
  OrderType,
  Position,
  PositionStatus,
  StopAdjustment,
  Strategy,
  StrategyParameter,
  StrategyParameterSet,
  TagKind,
  TradePlan,
  TradePlanStatus,
  WatchlistEntry,
  WatchlistLayer,
} from '@/types';

export interface StrategyWithParameterSet {
  strategy: Strategy;
  parameter_set: StrategyParameterSet | null;
}

interface DbStrategyRow {
  id: string;
  slug: string;
  name: string;
  version: number;
  asset_class: Strategy['asset_class'];
  strategy_type: Strategy['strategy_type'];
  setup_type: Strategy['setup_type'];
  default_stop_type: Strategy['default_stop_type'];
  data: Record<string, unknown>;
  is_active: boolean;
  is_planned: boolean;
  created_at: string;
  updated_at: string;
}

interface DbParameterSetRow {
  id: string;
  strategy_id: string;
  version: number;
  parameters: StrategyParameterSet['parameters'];
  is_default: boolean;
  notes: string;
  created_at: string;
}

function mapDbToStrategy(row: DbStrategyRow): Strategy {
  const data = row.data ?? {};
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    version: row.version,
    asset_class: row.asset_class,
    strategy_type: row.strategy_type,
    setup_type: row.setup_type,
    default_stop_type: row.default_stop_type,
    is_active: row.is_active,
    is_planned: row.is_planned,
    created_at: row.created_at,
    updated_at: row.updated_at,
    description_short: (data['description_short'] as string) ?? '',
    description_long: (data['description_long'] as string) ?? '',
    textbook_source: (data['textbook_source'] as string) ?? '',
    timeframe_primary: (data['timeframe_primary'] as Strategy['timeframe_primary']) ?? 'daily',
    timeframe_timing: (data['timeframe_timing'] as Strategy['timeframe_timing']) ?? 'daily',
    universe: (data['universe'] as Strategy['universe']) ?? {
      type: 'list',
      source: '',
      max_size: 0,
    },
    market_filter: (data['market_filter'] as Strategy['market_filter']) ?? {
      enabled: false,
      rules: [],
    },
    entry_rules: (data['entry_rules'] as Strategy['entry_rules']) ?? [],
    exit_rules: (data['exit_rules'] as Strategy['exit_rules']) ?? [],
    scoring_config: (data['scoring_config'] as Strategy['scoring_config']) ?? {
      score_type: 'swing_score',
      components: [],
    },
    parameter_set_id: (data['parameter_set_id'] as string) ?? '',
  };
}

function mapDbToParameterSet(row: DbParameterSetRow): StrategyParameterSet {
  return {
    id: row.id,
    strategy_id: row.strategy_id,
    version: row.version,
    parameters: row.parameters,
    is_default: row.is_default,
    notes: row.notes,
    created_at: row.created_at,
  };
}

/**
 * Lädt alle aktiven Strategien plus zugehörige Default-Parameter-Sets.
 */
export async function fetchActiveStrategiesWithParams(
  signal: AbortSignal
): Promise<StrategyWithParameterSet[]> {
  const { data: strategiesRaw, error: strategiesError } = await supabase
    .from(tableName('strategies'))
    .select('*')
    .eq('is_active', true)
    .order('name', { ascending: true })
    .abortSignal(signal);

  if (strategiesError) {
    throw Object.assign(new Error(strategiesError.message), { code: strategiesError.code });
  }
  if (!strategiesRaw || strategiesRaw.length === 0) return [];

  // Welle 7: V5.1 ist die einzige aktive Engine. V5-DB-Strategien werden
  // ausgefiltert, damit "Dual Momentum" & Co. nicht mehr in UI auftauchen.
  // Die DB-Einträge bleiben (für historische Backtest-Runs), nur die
  // Frontend-Sichtbarkeit ist entfernt.
  const filteredRaw = (strategiesRaw as DbStrategyRow[]).filter((row) => {
    // Alle 10 V5-Legacy strategy_types ausschließen.
    const legacyTypes = new Set([
      'time_series_momentum',
      'relative_strength',
      'dual_momentum',
      'twelve_one_momentum',
      'stage2_breakout',
      'stage2_continuation',
      'donchian_crypto',
      'cross_sectional_crypto',
      'deep_value_accumulation',
      'precious_metal_stage2',
    ]);
    return !legacyTypes.has(row.strategy_type as string);
  });
  if (filteredRaw.length === 0) return [];

  const strategies = filteredRaw.map(mapDbToStrategy);
  const paramSetIds = strategies.map((s) => s.parameter_set_id).filter((id) => Boolean(id));

  let paramSets: StrategyParameterSet[] = [];
  if (paramSetIds.length > 0) {
    const { data: paramRaw, error: paramError } = await supabase
      .from(tableName('strategy_parameter_sets'))
      .select('*')
      .in('id', paramSetIds)
      .abortSignal(signal);

    if (paramError) {
      throw Object.assign(new Error(paramError.message), { code: paramError.code });
    }
    paramSets = (paramRaw as DbParameterSetRow[] | null)?.map(mapDbToParameterSet) ?? [];
  }

  return strategies.map((strategy) => ({
    strategy,
    parameter_set: paramSets.find((ps) => ps.id === strategy.parameter_set_id) ?? null,
  }));
}

// ============================================================================
// Watchlist
// ============================================================================

export interface DbWatchlistRow {
  id: string;
  user_id: string;
  symbol: string;
  asset_class: AssetClass;
  layer: WatchlistLayer;
  data: Record<string, unknown>;
  added_at: string;
  last_evaluated_at: string | null;
  is_archived: boolean;
  tombstone_at: string | null;
  // V5.1 — neue Spalten (additiv, optional für V5-Rollback-Sicherheit)
  watchlist_role?: string | null;
  asset_subtype?: string | null;
  theme_tags?: string[] | null;
}

export function mapDbToWatchlistEntry(row: DbWatchlistRow): WatchlistEntry {
  const data = row.data ?? {};
  return {
    id: row.id,
    user_id: row.user_id,
    symbol: row.symbol,
    asset_class: row.asset_class,
    layer: row.layer,
    display_name: (data['display_name'] as string) ?? row.symbol,
    exchange: (data['exchange'] as string) ?? '',
    currency: (data['currency'] as string) ?? '',
    applicable_strategies: (data['applicable_strategies'] as string[]) ?? [],
    notes: (data['notes'] as string) ?? '',
    user_tags: (data['user_tags'] as string[]) ?? [],
    added_at: row.added_at,
    last_evaluated_at: row.last_evaluated_at ?? '',
    is_archived: row.is_archived,
    tombstone_at: row.tombstone_at,
    // V5.1 — neue Felder durchreichen (sonst sind Filter im V5.1-Modus null/undefined)
    watchlist_role: (row.watchlist_role ?? null) as WatchlistEntry['watchlist_role'],
    asset_subtype: (row.asset_subtype ?? null) as WatchlistEntry['asset_subtype'],
    theme_tags: (row.theme_tags ?? []) as WatchlistEntry['theme_tags'],
  };
}

/**
 * Lädt alle aktiven (nicht archivierten) Watchlist-Einträge des aktuellen
 * Users, gruppiert nach Layer.
 */
export async function fetchWatchlistEntries(
  signal: AbortSignal
): Promise<WatchlistEntry[]> {
  const { data, error } = await supabase
    .from(tableName('watchlist_entries'))
    .select('*')
    .eq('user_id', currentUserId())
    .eq('is_archived', false)
    .order('added_at', { ascending: false })
    .abortSignal(signal);

  if (error) {
    throw Object.assign(new Error(error.message), { code: error.code });
  }
  if (!data) return [];
  return (data as DbWatchlistRow[]).map(mapDbToWatchlistEntry);
}

export interface CreateWatchlistEntryInput {
  symbol: string;
  asset_class: AssetClass;
  layer: WatchlistLayer;
  display_name?: string;
  exchange?: string;
  currency?: string;
  applicable_strategies?: string[];
  notes?: string;
  user_tags?: string[];
}

/**
 * Legt einen neuen Watchlist-Eintrag an. Idempotent: ein bereits archivierter
 * Eintrag (Tombstone) wird wiederbelebt statt neu angelegt.
 */
export async function createWatchlistEntry(
  input: CreateWatchlistEntryInput
): Promise<WatchlistEntry> {
  const userId = currentUserId();
  const symbol = input.symbol.toUpperCase().trim();

  // Wenn ein archivierter Eintrag existiert, ihn revivieren statt zu duplizieren.
  const { data: existing } = await supabase
    .from(tableName('watchlist_entries'))
    .select('*')
    .eq('user_id', userId)
    .eq('symbol', symbol)
    .eq('layer', input.layer)
    .maybeSingle();

  const dataPayload = {
    display_name: input.display_name ?? symbol,
    exchange: input.exchange ?? '',
    currency: input.currency ?? '',
    applicable_strategies: input.applicable_strategies ?? [],
    notes: input.notes ?? '',
    user_tags: input.user_tags ?? [],
  };

  if (existing) {
    const { data: updated, error } = await supabase
      .from(tableName('watchlist_entries'))
      .update({
        is_archived: false,
        tombstone_at: null,
        // Audit 16.06.: watchlist_role mitsetzen — sonst zeigt ein revivierter
        // Eintrag mit altem/leerem role nicht im Tab (s. Insert-Fix unten).
        watchlist_role: input.layer,
        data: dataPayload,
        added_at: new Date().toISOString(),
      })
      .eq('id', (existing as DbWatchlistRow).id)
      .select('*')
      .single();
    if (error) throw new Error(error.message);
    return mapDbToWatchlistEntry(updated as DbWatchlistRow);
  }

  const { data: inserted, error } = await supabase
    .from(tableName('watchlist_entries'))
    .insert({
      user_id: userId,
      symbol,
      asset_class: input.asset_class,
      layer: input.layer,
      // Audit 16.06.: watchlist_role spiegeln. Die Watchlist filtert die Anzeige
      // nach watchlist_role === activeLayer (Watchlist.tsx). Ohne das tauchte ein
      // neu angelegter Eintrag in KEINEM Tab auf — der Bug, den das Experten-
      // Radar-Hinzufügen sichtbar gemacht hat (betraf aber JEDES Hinzufügen).
      watchlist_role: input.layer,
      data: dataPayload,
    })
    .select('*')
    .single();

  if (error) throw new Error(error.message);
  const row = mapDbToWatchlistEntry(inserted as DbWatchlistRow);
  // Welle 14 Polish — Activity-Log (fire-and-forget)
  void recordUserAction({
    action_type: 'watchlist_add',
    symbol: row.symbol,
    ref_id: row.id,
    details_summary: `${row.layer} / ${row.asset_class}`,
  });
  return row;
}

/**
 * Tombstone-Pattern (Welle T): wir löschen nicht physisch, sondern
 * setzen is_archived=true und tombstone_at=now().
 */
export async function archiveWatchlistEntry(id: string): Promise<void> {
  const { error } = await supabase
    .from(tableName('watchlist_entries'))
    .update({ is_archived: true, tombstone_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
  // Welle 14 Polish — Activity-Log (fire-and-forget)
  void recordUserAction({
    action_type: 'watchlist_remove',
    ref_id: id,
  });
}

/** Reaktiviert einen archivierten Eintrag (Undo). */
export async function restoreWatchlistEntry(id: string): Promise<void> {
  const { error } = await supabase
    .from(tableName('watchlist_entries'))
    .update({ is_archived: false, tombstone_at: null })
    .eq('id', id)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
}

/** Aktualisiert Notiz / Tags / Strategien-Zuordnung. */
export async function updateWatchlistEntry(
  id: string,
  patch: Partial<Pick<WatchlistEntry, 'notes' | 'user_tags' | 'applicable_strategies' | 'display_name'>>
): Promise<void> {
  // Wir müssen den jsonb-Datenblock mergen.
  const { data: row, error: readError } = await supabase
    .from(tableName('watchlist_entries'))
    .select('data')
    .eq('id', id)
    .eq('user_id', currentUserId())
    .single();
  if (readError) throw new Error(readError.message);

  const merged = { ...((row as { data: Record<string, unknown> }).data ?? {}), ...patch };
  const { error } = await supabase
    .from(tableName('watchlist_entries'))
    .update({ data: merged })
    .eq('id', id)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
}

// ============================================================================
// Strategy Parameter Sets — Versionierung
// ============================================================================

/**
 * Speichert ein neues Parameter-Set als nächste Version (v + 1).
 * Alte Versionen bleiben in der DB — wichtig für Backtest-Reproduzierbarkeit.
 * Setzt is_default auf true für die neue Version, false für die alte.
 *
 * Liefert die ID der neuen Parameter-Set-Row.
 */
export async function saveStrategyParameterSet(
  strategyId: string,
  parameters: StrategyParameterSet['parameters'],
  notes: string
): Promise<string> {
  // Aktuelle Default-Version finden, um deren version + 1 zu nutzen
  const { data: currentDefault, error: readError } = await supabase
    .from(tableName('strategy_parameter_sets'))
    .select('id, version')
    .eq('strategy_id', strategyId)
    .eq('is_default', true)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (readError) throw new Error(readError.message);

  const currentVersion = (currentDefault as { id: string; version: number } | null)?.version ?? 0;
  const newVersion = currentVersion + 1;

  // Neue Version anlegen
  const { data: inserted, error: insertError } = await supabase
    .from(tableName('strategy_parameter_sets'))
    .insert({
      strategy_id: strategyId,
      version: newVersion,
      parameters,
      is_default: true,
      notes: notes || `Version ${newVersion} — User-Edit`,
    })
    .select('id')
    .single();
  if (insertError) throw new Error(insertError.message);

  // Alte Default-Version markieren (nur als nicht-default, nicht löschen)
  if (currentDefault) {
    await supabase
      .from(tableName('strategy_parameter_sets'))
      .update({ is_default: false })
      .eq('id', (currentDefault as { id: string }).id);
  }

  // Strategy.parameter_set_id auf neue ID umstellen, damit zukünftige Reads
  // das neue Set bekommen.
  const newId = (inserted as { id: string }).id;
  const { data: stratRow, error: stratReadError } = await supabase
    .from(tableName('strategies'))
    .select('data')
    .eq('id', strategyId)
    .single();
  if (stratReadError) throw new Error(stratReadError.message);

  const stratData = (stratRow as { data: Record<string, unknown> }).data ?? {};
  const updatedData = { ...stratData, parameter_set_id: newId };
  await supabase
    .from(tableName('strategies'))
    .update({ data: updatedData })
    .eq('id', strategyId);

  return newId;
}

export interface StrategyVersionEntry {
  id: string;
  version: number;
  is_default: boolean;
  notes: string;
  created_at: string;
  parameters: StrategyParameter[];
}

/** Lädt die komplette Versionshistorie eines Strategy-ParameterSets. */
export async function fetchStrategyVersionHistory(
  strategyId: string,
  signal: AbortSignal
): Promise<StrategyVersionEntry[]> {
  const { data, error } = await supabase
    .from(tableName('strategy_parameter_sets'))
    .select('id, version, is_default, notes, created_at, parameters')
    .eq('strategy_id', strategyId)
    .order('version', { ascending: false })
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => {
    const row = r as {
      id: string;
      version: number;
      is_default: boolean;
      notes: string | null;
      created_at: string;
      parameters: StrategyParameter[];
    };
    return {
      id: row.id,
      version: row.version,
      is_default: row.is_default,
      notes: row.notes ?? '',
      created_at: row.created_at,
      parameters: row.parameters,
    };
  });
}

/**
 * Stellt eine alte Version wieder her, indem ihre Parameter als neue Version
 * (current+1) angelegt werden. Die alte Version bleibt unangetastet, die
 * Versionshistorie wächst monoton.
 */
export async function restoreStrategyParameterVersion(
  strategyId: string,
  sourceVersionId: string
): Promise<string> {
  const { data: src, error: readError } = await supabase
    .from(tableName('strategy_parameter_sets'))
    .select('version, parameters, notes')
    .eq('id', sourceVersionId)
    .single();
  if (readError) throw new Error(readError.message);
  const sourceVersion = (src as { version: number }).version;
  const sourceParams = (src as { parameters: StrategyParameter[] }).parameters;
  const sourceNotes = ((src as { notes: string | null }).notes ?? '').trim();
  const restoreNote = sourceNotes
    ? `Wiederhergestellt von v${sourceVersion}: ${sourceNotes}`
    : `Wiederhergestellt von v${sourceVersion}`;
  return saveStrategyParameterSet(strategyId, sourceParams, restoreNote);
}

// ============================================================================
// Trade-Plans
// ============================================================================

interface DbTradePlanRow {
  id: string;
  user_id: string;
  watchlist_entry_id: string | null;
  strategy_id: string | null;
  status: TradePlanStatus;
  /** V11.B — Order-Typ als eigene Spalte (statt jsonb), damit Server-Cron + Indexing funktioniert. */
  order_type: OrderType | null;
  trigger_price: number | null;
  data: Record<string, unknown>;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapDbToTradePlan(row: DbTradePlanRow): TradePlan {
  // Wir speichern den Großteil als jsonb. Die TradePlan-Felder, die nicht
  // doppelt sind, kommen aus row.data zurück.
  const data = (row.data ?? {}) as Partial<TradePlan>;
  return {
    ...(data as TradePlan),
    id: row.id,
    user_id: row.user_id,
    watchlist_entry_id: row.watchlist_entry_id ?? '',
    strategy_id: row.strategy_id ?? '',
    status: row.status,
    // V11.B — order_type/trigger_price aus dedizierten Spalten überschreiben
    // den jsonb-Wert (für Migrations-Rückwärtskompatibilität).
    order_type: row.order_type ?? data.order_type ?? 'market',
    trigger_price: row.trigger_price ?? data.trigger_price ?? null,
    expires_at: row.expires_at ?? '',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Speichert einen frisch generierten Trade-Plan. Liefert die DB-ID zurück. */
export async function insertTradePlan(plan: TradePlan): Promise<string> {
  // jsonb-Block: alles außer den dedizierten Spalten.
  // V11.B — order_type/trigger_price gehen in eigene Spalten.
  const {
    id: _id,
    user_id,
    watchlist_entry_id,
    strategy_id,
    status,
    order_type,
    trigger_price,
    expires_at,
    ...rest
  } = plan;
  void _id;

  const { data: inserted, error } = await supabase
    .from(tableName('trade_plans'))
    .insert({
      user_id: user_id || currentUserId(),
      watchlist_entry_id: watchlist_entry_id || null,
      strategy_id: strategy_id || null,
      status,
      order_type: order_type ?? 'market',
      trigger_price: trigger_price ?? null,
      data: rest,
      expires_at: expires_at || null,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  const newId = (inserted as { id: string }).id;
  // Welle 14 Polish — User-Action loggen (fire-and-forget)
  // symbol bleibt null hier; ref_id verlinkt zurück auf den Plan inkl. Symbol
  void recordUserAction({
    action_type: 'trade_plan_saved',
    ref_id: newId,
    details_summary: `Score ${plan.setup_score ?? '?'} · ${plan.setup_type ?? 'plan'}`,
  });
  return newId;
}

/** Lädt aktive (draft / active / triggered) Trade-Plans des aktuellen Users. */
/** Audit DD1: Plan-Snapshot per ID — auch wenn Status nicht mehr 'active' ist. */
export async function fetchTradePlanById(
  planId: string
): Promise<TradePlan | null> {
  const { data, error } = await supabase
    .from(tableName('trade_plans'))
    .select('*')
    .eq('id', planId)
    .eq('user_id', currentUserId())
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return mapDbToTradePlan(data as DbTradePlanRow);
}

/**
 * Setzt überfällige Pläne auf 'expired'.
 *
 * WARUM (Audit 10.08.2026): `expires_at` wurde gesetzt, angezeigt und in der
 * Revolut-Anleitung sogar als Gültigkeit versprochen (OrderFlowBlock.tsx) —
 * aber KEIN Schreiber hat den Status je auf 'expired' gesetzt. Pläne alterten
 * nie weg. Zwei Folgen: Die Engine konnte einen monatealten Plan noch
 * auslösen, und alte `active`-Leichen zählten weiter gegen das
 * Gesamtrisiko-Limit und sperrten damit NEUE Pläne (TradePlanCard.tsx).
 *
 * Läuft beim Engine-Durchgang mit, also beim App-Start und alle fünf Minuten.
 * Kein eigener Zeitplan, keine neue Abhängigkeit.
 *
 * Gibt die Zahl der abgelaufenen Pläne zurück — 0 ist der Normalfall.
 */
export async function expireUeberfaelligePlaene(): Promise<number> {
  const { data, error } = await supabase
    .from(tableName('trade_plans'))
    .update({ status: 'expired' as TradePlanStatus, updated_at: new Date().toISOString() })
    .eq('user_id', currentUserId())
    // 'triggered' bewusst NICHT: dahinter steht eine offene Position, die
    // nicht dadurch verschwindet, dass der Plan alt wird.
    .in('status', ['draft', 'active'])
    .not('expires_at', 'is', null)
    .lt('expires_at', new Date().toISOString())
    .select('id');
  if (error) throw new Error(error.message);
  return (data ?? []).length;
}

export async function fetchActiveTradePlans(signal: AbortSignal): Promise<TradePlan[]> {
  const { data, error } = await supabase
    .from(tableName('trade_plans'))
    .select('*')
    .eq('user_id', currentUserId())
    .in('status', ['draft', 'active', 'triggered'])
    .order('created_at', { ascending: false })
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapDbToTradePlan(r as DbTradePlanRow));
}

/**
 * PIPE.1: Lädt ALLE Trade-Pläne (alle Stati). Limit 200 — älteste Pläne fallen
 * unten ab, der User soll History sehen aber nicht jahrelang zurück.
 */
export async function fetchAllTradePlans(signal: AbortSignal): Promise<TradePlan[]> {
  const { data, error } = await supabase
    .from(tableName('trade_plans'))
    .select('*')
    .eq('user_id', currentUserId())
    .order('created_at', { ascending: false })
    .limit(200)
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapDbToTradePlan(r as DbTradePlanRow));
}

/** Status-Update für einen TradePlan (z.B. cancel, expire). */
export async function updateTradePlanStatus(
  id: string,
  status: TradePlanStatus
): Promise<void> {
  const { error } = await supabase
    .from(tableName('trade_plans'))
    .update({ status })
    .eq('id', id)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
}

// ============================================================================
// Positions
// ============================================================================

interface DbPositionRow {
  id: string;
  user_id: string;
  trade_plan_id: string | null;
  broker: Position['broker'];
  broker_account: string;
  broker_position_id: string | null;
  symbol: string;
  status: PositionStatus;
  data: Record<string, unknown>;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapDbToPosition(row: DbPositionRow): Position {
  const data = row.data as Partial<Position>;
  return {
    ...(data as Position),
    id: row.id,
    user_id: row.user_id,
    trade_plan_id: row.trade_plan_id,
    broker: row.broker,
    broker_account: row.broker_account,
    broker_position_id: row.broker_position_id,
    symbol: row.symbol,
    status: row.status,
    last_synced_at: row.last_synced_at ?? '',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export async function fetchPositions(
  status: 'open' | 'closed' | 'all',
  signal: AbortSignal
): Promise<Position[]> {
  let query = supabase
    .from(tableName('positions'))
    .select('*')
    .eq('user_id', currentUserId())
    .order('created_at', { ascending: false });
  if (status === 'open') query = query.in('status', ['open', 'partially_closed']);
  if (status === 'closed') query = query.eq('status', 'closed');
  const { data, error } = await query.abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapDbToPosition(r as DbPositionRow));
}

export interface OpenPositionInput {
  trade_plan_id: string | null;
  broker: Position['broker'];
  broker_account: string;
  symbol: string;
  asset_class: AssetClass;
  side: Position['side'];
  quantity: number;
  avg_entry_price: number;
  entry_at: string;
  entry_fees: number;
  trade_currency: string;
  account_currency: string;
  fx_rate_at_entry: number;
  current_stop: number;
  initial_stop: number;
  /* MM1 — Take-Profit-Targets aus Plan */
  take_profit_1?: number | null;
  take_profit_2?: number | null;
  take_profit_3?: number | null;
  /* MM1 — Herkunft */
  created_by?: 'manual' | 'paper_engine' | 'ibkr' | 'experiment' | 'signal_papier';
  /* UU2 — Score-Snapshot beim Entry */
  entry_score_snapshot?: number | null;
  entry_strategy_slug?: string | null;
  /* M5 — Time-Stop in Tagen nach Entry (NULL = kein Time-Stop) */
  max_hold_days?: number | null;
}

/** Erstellt eine offene Position. */
export async function insertPosition(input: OpenPositionInput): Promise<string> {
  const now = new Date().toISOString();
  const data = {
    side: input.side,
    quantity: input.quantity,
    avg_entry_price: input.avg_entry_price,
    entry_at: input.entry_at,
    entry_fees: input.entry_fees,
    trade_currency: input.trade_currency,
    account_currency: input.account_currency,
    fx_rate_at_entry: input.fx_rate_at_entry,
    avg_entry_price_account_currency: input.avg_entry_price * input.fx_rate_at_entry,
    current_price: null,
    current_pnl_abs_native: null,
    current_pnl_abs_account: null,
    current_pnl_pct: null,
    current_r_multiple: null,
    unrealized_pnl_account: null,
    current_stop: input.current_stop,
    initial_stop: input.initial_stop,
    stop_history: [],
    exit_at: null,
    exit_price: null,
    exit_reason: null,
    fx_rate_at_exit: null,
    realized_pnl_native: null,
    realized_pnl_account: null,
    exit_fees: null,
    final_r_multiple: null,
    monitoring_score: null,
    monitoring_score_explanation: null,
    setup_quality_intact: true,
    trail_stop_suggestion: null,
    partial_take_suggestion: null,
    asset_class: input.asset_class,
    /* MM1 — Take-Profit-Tracking */
    take_profit_1: input.take_profit_1 ?? null,
    take_profit_2: input.take_profit_2 ?? null,
    take_profit_3: input.take_profit_3 ?? null,
    tp1_hit: false,
    tp2_hit: false,
    tp3_hit: false,
    /* MM1 — Herkunft */
    created_by: input.created_by ?? 'manual',
    /* UU2 — Score-Snapshot beim Entry */
    entry_score_snapshot: input.entry_score_snapshot ?? null,
    entry_strategy_slug: input.entry_strategy_slug ?? null,
    /* M5 — Time-Stop (Tage). NULL = kein Time-Stop. */
    max_hold_days: input.max_hold_days ?? null,
  };
  const { data: inserted, error } = await supabase
    .from(tableName('positions'))
    .insert({
      user_id: currentUserId(),
      trade_plan_id: input.trade_plan_id,
      broker: input.broker,
      broker_account: input.broker_account,
      broker_position_id: null,
      symbol: input.symbol.toUpperCase(),
      status: 'open' as PositionStatus,
      data,
      last_synced_at: now,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (inserted as { id: string }).id;
}

/**
 * Audit RR1: Backfill — wenn eine Position keine Take-Profits gesetzt hat,
 * aber einen verknüpften Trade-Plan, ziehen wir TP1/TP2/TP3 aus dem Plan in
 * die Position. Idempotent: tut nichts, wenn TPs schon gesetzt sind oder
 * Plan keine hat.
 */
export async function backfillPositionTakeProfits(positionId: string): Promise<{
  filled: boolean;
  tp1: number | null;
  tp2: number | null;
  tp3: number | null;
}> {
  const { data: row, error: readError } = await supabase
    .from(tableName('positions'))
    .select('trade_plan_id, data')
    .eq('id', positionId)
    .eq('user_id', currentUserId())
    .single();
  if (readError) throw new Error(readError.message);
  const r = row as { trade_plan_id: string | null; data: Record<string, unknown> };
  const data = r.data ?? {};
  const tp1Existing = data['take_profit_1'];
  const tp2Existing = data['take_profit_2'];
  const tp3Existing = data['take_profit_3'];
  // Wenn schon irgendwas gesetzt ist, nichts tun.
  if (tp1Existing != null || tp2Existing != null || tp3Existing != null) {
    return {
      filled: false,
      tp1: typeof tp1Existing === 'number' ? tp1Existing : null,
      tp2: typeof tp2Existing === 'number' ? tp2Existing : null,
      tp3: typeof tp3Existing === 'number' ? tp3Existing : null,
    };
  }
  if (!r.trade_plan_id) {
    return { filled: false, tp1: null, tp2: null, tp3: null };
  }
  const plan = await fetchTradePlanById(r.trade_plan_id);
  if (!plan) return { filled: false, tp1: null, tp2: null, tp3: null };
  const tp1 = plan.take_profit_1 ?? null;
  const tp2 = plan.take_profit_2 ?? null;
  const tp3 = plan.take_profit_3 ?? null;
  if (tp1 === null && tp2 === null && tp3 === null) {
    return { filled: false, tp1: null, tp2: null, tp3: null };
  }
  const merged = {
    ...data,
    take_profit_1: tp1,
    take_profit_2: tp2,
    take_profit_3: tp3,
    tp1_hit: data['tp1_hit'] ?? false,
    tp2_hit: data['tp2_hit'] ?? false,
    tp3_hit: data['tp3_hit'] ?? false,
  };
  const { error } = await supabase
    .from(tableName('positions'))
    .update({ data: merged, updated_at: new Date().toISOString() })
    .eq('id', positionId)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
  return { filled: true, tp1, tp2, tp3 };
}

/** Stop nachziehen + Stop-History anhängen. */
export async function adjustPositionStop(
  positionId: string,
  newStop: number,
  reason: string,
  trigger: StopAdjustment['triggered_by']
): Promise<void> {
  const { data: row, error: readError } = await supabase
    .from(tableName('positions'))
    .select('data')
    .eq('id', positionId)
    .eq('user_id', currentUserId())
    .single();
  if (readError) throw new Error(readError.message);
  const currentData = (row as { data: Record<string, unknown> }).data;
  const currentStop = (currentData['current_stop'] as number) ?? 0;
  const history = ((currentData['stop_history'] as StopAdjustment[]) ?? []).slice();
  history.push({
    at: new Date().toISOString(),
    from: currentStop,
    to: newStop,
    reason,
    triggered_by: trigger,
  });
  const merged = { ...currentData, current_stop: newStop, stop_history: history };
  const { error } = await supabase
    .from(tableName('positions'))
    .update({ data: merged })
    .eq('id', positionId)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
}

/**
 * Nachkauf-Tranche auf eine bestehende offene Position (20.09.2026,
 * Pyramiden-Einstieg — Ronni: mehrere Revolut-Teilkäufe als EINE wachsende
 * Position statt fünf einzelne). Mittelt Einstiegspreis und Wechselkurs
 * gewichtet nach Stückzahl (`gewichteterEinstieg` in v6/pyramide.ts),
 * summiert Stückzahl und Gebühren.
 *
 * DER STOP ÄNDERT SICH NICHT — das ist die ganze Idee der Pyramide: alle
 * Tranchen teilen denselben Stop, nur der Einstiegspreis (und damit das
 * Risiko je Stück) unterscheidet sich. Ein Nachkauf mit ANDEREM Stop wäre
 * kein Pyramiden-Tranchen-Kauf mehr, sondern ein neuer Trade — dafür bleibt
 * `insertPosition`.
 *
 * Read-modify-write auf `data`, exakt das Muster aus `adjustPositionStop`
 * oben — keine zweite Update-Konvention für dieselbe Spalte.
 */
export async function nachkaufPosition(
  positionId: string,
  tranche: { stueck: number; einstieg: number; fxRate: number; gebuehren: number }
): Promise<void> {
  const { data: row, error: readError } = await supabase
    .from(tableName('positions'))
    .select('data')
    .eq('id', positionId)
    .eq('user_id', currentUserId())
    .single();
  if (readError) throw new Error(readError.message);
  const currentData = (row as { data: Record<string, unknown> }).data;

  const bestehendeStueck = Number(currentData['quantity']) || 0;
  const bestehenderEinstieg = Number(currentData['avg_entry_price']) || 0;
  const bestehendeFxRate = Number(currentData['fx_rate_at_entry']) || 1;
  const bestehendeGebuehren = Number(currentData['entry_fees']) || 0;

  const neueStueck = bestehendeStueck + tranche.stueck;
  const neuerEinstieg = gewichteterEinstieg(
    bestehendeStueck,
    bestehenderEinstieg,
    tranche.stueck,
    tranche.einstieg
  );
  const neueFxRate = gewichteterEinstieg(
    bestehendeStueck,
    bestehendeFxRate,
    tranche.stueck,
    tranche.fxRate
  );

  const merged = {
    ...currentData,
    quantity: neueStueck,
    avg_entry_price: neuerEinstieg,
    avg_entry_price_account_currency: neuerEinstieg * neueFxRate,
    fx_rate_at_entry: neueFxRate,
    entry_fees: bestehendeGebuehren + tranche.gebuehren,
  };
  const { error } = await supabase
    .from(tableName('positions'))
    .update({ data: merged, updated_at: new Date().toISOString() })
    .eq('id', positionId)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
}

/** Position manuell schließen. */
/**
 * Zusatzangaben für den Journaleintrag, den `closePosition` schreibt.
 *
 * Wer einen reicheren Bericht hat — etwa den Plan-vs-Ausführung-Abgleich —
 * baut ihn VOR dem Schliessen (alle Zahlen sind da: Plan, Position, Exitpreis,
 * Grund, Gebühren) und reicht ihn hier durch. So schreibt genau EINE Stelle
 * genau EINEN `exit`-Eintrag. Würde der Aufrufer zusätzlich selbst schreiben,
 * gäbe es zwei Zeilen pro Trade und jede Auswertung auf `action_type='exit'`
 * zählte doppelt.
 */
export interface CloseJournalZusatz {
  titel?: string;
  markdown?: string;
  setup_type?: JournalEntry['setup_type'];
  market_phase?: JournalEntry['market_phase'];
  rule_followed?: boolean;
  discipline_score?: number;
  error_tags?: string[];
  positive_tags?: string[];
  trade_plan_id?: string | null;
  /**
   * Saetze aus der Trade-Obduktion (src/lib/v6/trade-obduktion.ts), etwa
   * „Stop war schon am 14.03. verletzt" oder „Teilverkauf bei +2R war faellig".
   *
   * Sie werden an den Journaltext ANGEHAENGT, nicht als eigene Zeile
   * geschrieben: Zwei Eintraege zum selben Ausstieg wuerden jede Auswertung
   * doppelt zaehlen. Und sie werden HIER festgeschrieben statt bei jedem
   * Seitenaufruf neu gerechnet — die Obduktionskarte tut Letzteres, und
   * dadurch aendert sich ihr Urteil rueckwirkend, sobald sich die Historie
   * aendert. Was beim Schliessen galt, soll nachlesbar bleiben.
   */
  befunde?: string[];
}

/**
 * Schliesst eine Position UND schreibt den Journaleintrag dazu.
 *
 * WARUM DER JOURNALEINTRAG HIER LIEGT (Audit 10.08.2026): Vorher schrieb ihn
 * der Dialog auf der Positionen-Seite — und zwar nur dann, wenn die Position
 * eine `trade_plan_id` hatte UND der Plan noch ladbar war. Von fünf Wegen,
 * eine Position zu schliessen, hinterliess also genau einer eine Spur: der
 * Banner-Close nicht, die drei automatischen Engine-Closes nicht. Der
 * Dateikopf der Paper-Engine behauptete dabei das Gegenteil.
 *
 * Folge: Jede Auswertung auf `action_type='exit'` rechnete auf einer
 * verzerrten Teilmenge — und der Nutzer sah „im Journal läuft nichts rein".
 * Am Datenlayer kann kein Aufrufer es mehr vergessen.
 */
export async function closePosition(
  positionId: string,
  exitPrice: number,
  exitReason: ExitReason,
  exitFees: number,
  journal?: CloseJournalZusatz
): Promise<void> {
  // V11.A.4: trade_plan_id mitlesen für Plan-Status-Sync
  const { data: row, error: readError } = await supabase
    .from(tableName('positions'))
    // `symbol` ist eine eigene Spalte, nicht Teil von `data` — ohne sie
    // stuende im Journaltitel nur "Position".
    .select('data, trade_plan_id, symbol')
    .eq('id', positionId)
    .eq('user_id', currentUserId())
    .single();
  if (readError) throw new Error(readError.message);
  const positionRow = row as {
    data: Record<string, unknown>;
    trade_plan_id: string | null;
    symbol: string | null;
  };
  const currentData = positionRow.data;
  const entryPrice = (currentData['avg_entry_price'] as number) ?? 0;
  const initStop = (currentData['initial_stop'] as number) ?? entryPrice;
  const quantity = (currentData['quantity'] as number) ?? 0;
  const fxAtEntry = (currentData['fx_rate_at_entry'] as number) ?? 1;
  const initRisk = entryPrice - initStop;
  const finalR = initRisk > 0 ? (exitPrice - entryPrice) / initRisk : 0;
  const realizedPnlNative = (exitPrice - entryPrice) * quantity - exitFees;
  const realizedPnlAccount = realizedPnlNative * fxAtEntry;

  const merged = {
    ...currentData,
    exit_at: new Date().toISOString(),
    exit_price: exitPrice,
    exit_reason: exitReason,
    fx_rate_at_exit: fxAtEntry, // simpler: gleich wie entry, korrekt wäre live FX
    realized_pnl_native: realizedPnlNative,
    realized_pnl_account: realizedPnlAccount,
    exit_fees: exitFees,
    final_r_multiple: finalR,
  };

  const { error } = await supabase
    .from(tableName('positions'))
    .update({ status: 'closed' as PositionStatus, data: merged })
    .eq('id', positionId)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);

  // V11.A.4 — Plan-Status synchronisieren beim manuellen Position-Close.
  // Verhindert Phantom-Pläne (Plan auf 'triggered' ohne offene Position).
  if (positionRow.trade_plan_id) {
    await updateTradePlanStatus(positionRow.trade_plan_id, 'closed');
  }

  // Journaleintrag — ab hier auf JEDEM Weg, nicht mehr nur im Dialog.
  //
  // Er darf den Close nicht scheitern lassen: Die Position IST geschlossen,
  // und ein Fehler beim Protokollieren dürfte diese Tatsache nicht in Frage
  // stellen. Er wird aber gemeldet statt verschluckt — genau dieses stille
  // Verschlucken (Positions.tsx, `console.error` im catch) war der Grund,
  // warum die Lücke so lange unbemerkt blieb.
  try {
    const symbol = positionRow.symbol ?? '';
    const vorzeichen = finalR >= 0 ? '+' : '';
    await insertJournalEntry({
      position_id: positionId,
      trade_plan_id: journal?.trade_plan_id ?? positionRow.trade_plan_id,
      date: new Date().toISOString().slice(0, 10),
      action_type: 'exit',
      title:
        journal?.titel
        ?? `Exit ${symbol || 'Position'} (${exitReason}) — ${vorzeichen}${finalR.toFixed(2)}R`,
      body_markdown:
        (journal?.markdown
        ?? [
          `**Position geschlossen.**`,
          '',
          `- Grund: ${exitReason}`,
          `- Einstieg: ${entryPrice.toFixed(2)} · Ausstieg: ${exitPrice.toFixed(2)}`,
          `- Anfangsstop: ${initStop.toFixed(2)} (R = ${initRisk.toFixed(2)})`,
          `- Ergebnis: ${vorzeichen}${finalR.toFixed(2)}R, `
            + `${realizedPnlAccount >= 0 ? '+' : ''}${realizedPnlAccount.toFixed(2)}`,
          '',
          '**Was war der Auslöser für den Ausstieg?** _Bitte ergänzen._',
        ].join('\n'))
        + (journal?.befunde?.length
          ? '\n\n**Obduktion (Stand des Schliessens):**\n'
            + journal.befunde.map((b) => `- ${b}`).join('\n')
          : ''),
      setup_type: journal?.setup_type ?? null,
      market_phase: journal?.market_phase ?? 'transition',
      rule_followed: journal?.rule_followed ?? true,
      discipline_score: journal?.discipline_score ?? 0,
      emotional_state: null,
      error_tags: journal?.error_tags ?? [],
      positive_tags: journal?.positive_tags ?? [],
      plan_vs_execution: null,
      attached_chart_url: null,
    });
  } catch (e) {
    logCaughtError('closePosition.journal', e, {
      severity: 'warn',
      context: { positionId, exitReason },
    });
  }
}

// ============================================================================
// Briefing-History
// ============================================================================

export interface DbBriefingRow {
  id: string;
  symbol: string;
  asset_class: BriefingHistoryEntry['asset_class'];
  score_type: BriefingHistoryEntry['score_type'];
  briefing_date: string;
  data: Record<string, unknown>;
  previous_briefing_id: string | null;
  hit_rate_outcome: BriefingHistoryEntry['hit_rate_outcome'] | null;
  created_at: string;
}

export function mapDbToBriefing(row: DbBriefingRow): BriefingHistoryEntry {
  const data = row.data as Partial<BriefingHistoryEntry>;
  return {
    ...(data as BriefingHistoryEntry),
    id: row.id,
    symbol: row.symbol,
    asset_class: row.asset_class,
    score_type: row.score_type,
    briefing_date: row.briefing_date,
    previous_briefing_id: row.previous_briefing_id,
    hit_rate_outcome: row.hit_rate_outcome,
    created_at: row.created_at,
  };
}

export async function fetchBriefingHistory(
  symbol: string,
  signal: AbortSignal
): Promise<BriefingHistoryEntry[]> {
  const { data, error } = await supabase
    .from(tableName('briefing_history'))
    .select('*')
    .eq('symbol', symbol)
    .order('briefing_date', { ascending: false })
    .limit(20)
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapDbToBriefing(r as DbBriefingRow));
}

// ============================================================================
// Backtest-Runs
// ============================================================================

// ============================================================================
// Journal
// ============================================================================

interface DbJournalRow {
  id: string;
  user_id: string;
  position_id: string | null;
  trade_plan_id: string | null;
  action_type: JournalActionType;
  data: Record<string, unknown>;
  date: string;
  created_at: string;
}

function mapDbToJournal(row: DbJournalRow): JournalEntry {
  const data = row.data as Partial<JournalEntry>;
  return {
    ...(data as JournalEntry),
    id: row.id,
    user_id: row.user_id,
    position_id: row.position_id,
    trade_plan_id: row.trade_plan_id,
    action_type: row.action_type,
    date: row.date,
    created_at: row.created_at,
  };
}

export async function fetchJournalEntries(signal: AbortSignal): Promise<JournalEntry[]> {
  const { data, error } = await supabase
    .from(tableName('journal_entries'))
    .select('*')
    .eq('user_id', currentUserId())
    .order('date', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(200)
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapDbToJournal(r as DbJournalRow));
}

export async function insertJournalEntry(
  entry: Omit<JournalEntry, 'id' | 'created_at' | 'user_id'>
): Promise<string> {
  const { position_id, trade_plan_id, action_type, date, ...rest } = entry;
  const { data: inserted, error } = await supabase
    .from(tableName('journal_entries'))
    .insert({
      user_id: currentUserId(),
      position_id: position_id || null,
      trade_plan_id: trade_plan_id || null,
      action_type,
      date,
      data: rest,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (inserted as { id: string }).id;
}

export async function deleteJournalEntry(id: string): Promise<void> {
  const { error } = await supabase
    .from(tableName('journal_entries'))
    .delete()
    .eq('id', id)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
}

interface DbJournalTagRow {
  key: string;
  kind: TagKind;
  display_name: string;
  description: string;
  is_user_added: boolean;
  is_system: boolean;
  created_at: string;
}

export async function fetchJournalTags(signal: AbortSignal): Promise<JournalTag[]> {
  const { data, error } = await supabase
    .from(tableName('journal_tags'))
    .select('*')
    .order('kind')
    .order('display_name')
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []) as DbJournalTagRow[];
}

export async function addUserTag(
  key: string,
  kind: TagKind,
  display_name: string,
  description: string
): Promise<void> {
  const { error } = await supabase
    .from(tableName('journal_tags'))
    .insert({
      key,
      kind,
      display_name,
      description,
      is_user_added: true,
      is_system: false,
    });
  if (error) throw new Error(error.message);
}

// ============================================================================
// Lessons Learned
// ============================================================================

interface DbLessonRow {
  id: string;
  user_id: string;
  source: 'auto' | 'manual';
  source_journal_entry_ids: string[];
  data: Record<string, unknown>;
  is_pinned: boolean;
  is_resolved: boolean;
  created_at: string;
}

function mapDbToLesson(row: DbLessonRow): LessonsLearned {
  const data = row.data as Partial<LessonsLearned>;
  return {
    ...(data as LessonsLearned),
    id: row.id,
    user_id: row.user_id,
    source: row.source,
    source_journal_entry_ids: row.source_journal_entry_ids,
    is_pinned: row.is_pinned,
    is_resolved: row.is_resolved,
    created_at: row.created_at,
  };
}

export async function fetchLessons(signal: AbortSignal): Promise<LessonsLearned[]> {
  const { data, error } = await supabase
    .from(tableName('lessons_learned'))
    .select('*')
    .eq('user_id', currentUserId())
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(50)
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapDbToLesson(r as DbLessonRow));
}

export async function insertLesson(
  lesson: Omit<LessonsLearned, 'id' | 'created_at' | 'user_id'>
): Promise<string> {
  const { source, source_journal_entry_ids, is_pinned, is_resolved, ...rest } = lesson;
  const { data: inserted, error } = await supabase
    .from(tableName('lessons_learned'))
    .insert({
      user_id: currentUserId(),
      source,
      source_journal_entry_ids,
      data: rest,
      is_pinned,
      is_resolved,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (inserted as { id: string }).id;
}

export async function updateLessonStatus(
  id: string,
  patch: Partial<Pick<LessonsLearned, 'is_pinned' | 'is_resolved' | 'resolution_note'>>
): Promise<void> {
  const dbPatch: Record<string, unknown> = {};
  if (patch.is_pinned !== undefined) dbPatch.is_pinned = patch.is_pinned;
  if (patch.is_resolved !== undefined) dbPatch.is_resolved = patch.is_resolved;

  if (patch.resolution_note !== undefined) {
    // resolution_note steckt in jsonb data — wir mergen
    const { data: row, error: readError } = await supabase
      .from(tableName('lessons_learned'))
      .select('data')
      .eq('id', id)
      .eq('user_id', currentUserId())
      .single();
    if (readError) throw new Error(readError.message);
    const merged = {
      ...((row as { data: Record<string, unknown> }).data ?? {}),
      resolution_note: patch.resolution_note,
    };
    dbPatch.data = merged;
  }

  const { error } = await supabase
    .from(tableName('lessons_learned'))
    .update(dbPatch)
    .eq('id', id)
    .eq('user_id', currentUserId());
  if (error) throw new Error(error.message);
}

/** Aktualisiert die ai_interpretation eines BacktestRuns. */

/** Lädt die N neuesten Briefings über alle Symbole (Dashboard-Aggregation). */
export async function fetchLatestBriefings(
  limit: number,
  signal: AbortSignal
): Promise<BriefingHistoryEntry[]> {
  const { data, error } = await supabase
    .from(tableName('briefing_history'))
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)
    .abortSignal(signal);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapDbToBriefing(r as DbBriefingRow));
}

// V8.BRIEF.1 + Z.6.NOW — Tages-Briefing (Cron-erzeugt). Pro Tag bis zu 2 Zeilen:
// scope='stocks' (Aktien/ETFs) und scope='crypto' (Krypto). Wochenende nur 'crypto'.
// Alte Einträge aus der Vor-Z.6-Phase haben scope='all' (Migration-Default).
export type BriefingScope = 'stocks' | 'crypto' | 'all';

export interface DailyBriefingNewsSpikeItem {
  symbol: string;
  /** W36-QW3: Klartext-Name der Aktie damit User die Aktie ohne Ticker-Kenntnis erkennt */
  display_name?: string | null;
  count_7d: number | null;
  top_headline: string | null;
  top_headline_url: string | null;
  buzz_score: number | null;
  sentiment_bullish_pct: number | null;
  sentiment_bearish_pct: number | null;
}

export interface DailyBriefingRow {
  briefing_date: string;
  scope: BriefingScope;
  generated_at: string;
  summary: string | null;
  market_recap: string | null;
  watchlist_impact: string | null;
  position_impact: string | null;
  news_macro: string | null;
  tomorrow_focus: string | null;
  critical_items: string[] | null;
  observe_items: string[] | null;
  data_gaps: string[] | null;
  score_changes: Array<{ symbol: string; change: string }> | null;
  // Welle 13 Phase F — News-Spike-Daten
  news_spike: string | null;
  news_spike_items: DailyBriefingNewsSpikeItem[] | null;
  source_model: string | null;
  cost_usd: number | null;
  error: string | null;
}

/**
 * Holt das jüngste Tages-Briefing für einen bestimmten Scope.
 * Cron schreibt täglich 16:00 UTC. Liefert null, wenn für den Scope noch kein
 * Briefing existiert.
 */
export async function fetchLatestDailyBriefing(
  scope: BriefingScope,
  signal: AbortSignal
): Promise<DailyBriefingRow | null> {
  let query = supabase
    .from(tableName('daily_briefing_history'))
    .select('*')
    .order('briefing_date', { ascending: false })
    .limit(1);
  if (scope === 'all') {
    query = query.eq('scope', 'all');
  } else {
    query = query.eq('scope', scope);
  }
  const { data, error } = await query.abortSignal(signal);
  if (error) throw new Error(error.message);
  const row = data?.[0] as DailyBriefingRow | undefined;
  return row ?? null;
}

// ============================================================================
// User-Settings (Konto-Größe, Risk-Defaults)
// ============================================================================

export interface UserSettings {
  account_balance: number;
  account_currency: 'EUR' | 'USD';
  default_risk_per_trade_pct: number;
  /** Maximaler offener Risiko-Anteil über alle Pläne — Schutz gegen Cluster */
  max_total_open_risk_pct: number;
  /** Default-Layer beim Add-Dialog */
  default_watchlist_layer: 'swing' | 'longterm_build' | 'sector_etf' | 'crypto_accumulation';
  /** Lieblings-Asset-Klasse beim Add-Dialog */
  default_asset_class: 'stock' | 'etf' | 'crypto';
  /** Email-Adresse für Alerts (Phase 4 — derzeit nur gespeichert, nicht genutzt) */
  notification_email: string | null;
  /** Audit 2026-04-29: Broker-Fähigkeit für Fractional Shares.
   *  none = nur ganze Stücke (Standard für Sparkasse/comdirect/Trade Republic Vanilla);
   *  fractional_01 = 1 Nachkommastelle (IBKR-EU Fractional Shares aktiviert);
   *  fractional_001 = 2 Nachkommastellen (IBKR-US, manche US-Broker);
   *  cash = cash-basierte Order (Trade Republic Sparplan, Revolut). */
  fractional_mode: 'none' | 'fractional_01' | 'fractional_001' | 'cash';
  /** Geschätzte Mindestgebühr pro Order in Account-Currency. Bei kleinen Konten
   *  und Fractional-Trades entscheidend für Wirtschaftlichkeit. */
  min_order_fee_account: number;
  /** MAIL.3 — Email-Präferenzen pro Mail-Typ. Default: alle aus, User aktiviert
   *  in den Settings selbst. Damit niemand ungewollt Mails bekommt. */
  email_prefs?: {
    daily_digest?: boolean;
    instant_alerts?: boolean;
    weekly_digest?: boolean;
    /** Schwellwert für Sofort-Alerts: ab welchem Score (0-100) eine Mail rausgeht. */
    instant_alert_score_threshold?: number;
    /** IANA-Timezone-String, z. B. "Europe/Berlin". Steuert, wann Cron lokal "morgens" wirkt. */
    timezone?: string;
    /** W61 — Setup-Alerts Morgen-Mail (Mo-Fr 13:00 UTC vor US-Open) */
    setup_alerts?: boolean;
  };
  /**
   * V5.1 — Engine-Version Feature-Flag.
   * 'v5' = klassische Watchlist-Layer + Score-Engine + Backtest (Bestand)
   * 'v51' = neue Multi-Score-Architektur, Marktphasen-Engine, Pure-Function-Strategien
   * Default: 'v51' (neue Engine ist Default), User kann auf 'v5' zurück.
   */
  engine_version?: 'v5' | 'v51';
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  account_balance: 10000,
  account_currency: 'EUR',
  default_risk_per_trade_pct: 0.5,
  max_total_open_risk_pct: 5,
  default_watchlist_layer: 'swing',
  default_asset_class: 'stock',
  notification_email: null,
  fractional_mode: 'none',
  min_order_fee_account: 1.5,
  email_prefs: {
    daily_digest: false,
    instant_alerts: false,
    weekly_digest: false,
    instant_alert_score_threshold: 75,
    timezone: 'Europe/Berlin',
  },
  engine_version: 'v51',
};

export async function fetchUserSettings(signal: AbortSignal): Promise<UserSettings> {
  void signal; // maybeSingle hat in dieser supabase-js-Version keinen abortSignal-Builder
  const { data, error } = await supabase
    .from(tableName('user_settings'))
    .select('data')
    .eq('user_id', currentUserId())
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return DEFAULT_USER_SETTINGS;
  return {
    ...DEFAULT_USER_SETTINGS,
    ...((data as { data: Record<string, unknown> }).data as Partial<UserSettings>),
  };
}

export async function saveUserSettings(settings: UserSettings): Promise<void> {
  const { error } = await supabase
    .from(tableName('user_settings'))
    .upsert(
      {
        user_id: currentUserId(),
        data: settings as unknown as Record<string, unknown>,
      },
      { onConflict: 'user_id' }
    );
  if (error) throw new Error(error.message);
}

export async function insertBriefing(
  briefing: Omit<BriefingHistoryEntry, 'id' | 'created_at'>
): Promise<string> {
  const {
    symbol,
    asset_class,
    score_type,
    briefing_date,
    previous_briefing_id,
    hit_rate_outcome,
    ...rest
  } = briefing;

  const { data: inserted, error } = await supabase
    .from(tableName('briefing_history'))
    .insert({
      // Welle ZB: user_id explizit für RLS-INSERT
      user_id: currentUserId(),
      symbol,
      asset_class,
      score_type,
      briefing_date,
      data: rest,
      previous_briefing_id: previous_briefing_id ?? null,
      hit_rate_outcome: hit_rate_outcome ?? null,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (inserted as { id: string }).id;
}
